let swRegisterPromise = null;
let swReloadPending = false;

export function isReactSnap() {
  return typeof navigator !== "undefined" && /ReactSnap/i.test(String(navigator.userAgent || ""));
}

export function isSecureForServiceWorker() {
  if (typeof window === "undefined") return false;
  if (window.isSecureContext) return true;
  const host = String(window.location.hostname || "").toLowerCase();
  return host === "localhost" || host === "127.0.0.1";
}

function readEnv(key, fallback = "") {
  const env = typeof process !== "undefined" && process?.env ? process.env : {};
  return String(env[key] ?? fallback).trim();
}

export function isServiceWorkerEnabledByEnv() {
  const explicit = readEnv("REACT_APP_ENABLE_SW", "");
  if (explicit) {
    const normalized = explicit.toLowerCase();
    if (["0", "false", "no", "off"].includes(normalized)) return false;
    if (["1", "true", "yes", "on"].includes(normalized)) return true;
  }

  if (readEnv("NODE_ENV", "development") === "production") return true;

  // Safety net for mis-deployed CRA builds: when a compiled bundle is served from
  // a real HTTPS production domain, SW must not be treated as dev-disabled.
  if (typeof window !== "undefined") {
    const host = String(window.location.hostname || "").toLowerCase();
    const isLocal = host === "localhost" || host === "127.0.0.1" || host === "0.0.0.0" || host.endsWith(".local");
    if (!isLocal && window.isSecureContext) return true;
  }

  return false;
}

export function serviceWorkerUrl() {
  const env = typeof process !== "undefined" && process?.env ? process.env : {};
  return String(env.REACT_APP_SERVICE_WORKER_URL || "/sw.js").trim() || "/sw.js";
}

export function getServiceWorkerDiagnostics() {
  const hasWindow = typeof window !== "undefined";
  const hasNavigator = typeof navigator !== "undefined";
  const supported = hasNavigator && "serviceWorker" in navigator;
  const secureContext = isSecureForServiceWorker();
  const enabledByEnv = isServiceWorkerEnabledByEnv();
  const reactSnap = isReactSnap();

  return {
    supported,
    secureContext,
    enabledByEnv,
    reactSnap,
    canRegister: Boolean(hasWindow && supported && secureContext && enabledByEnv && !reactSnap),
    url: serviceWorkerUrl(),
  };
}

export function canUseServiceWorker() {
  return getServiceWorkerDiagnostics().canRegister;
}

function activateWaitingServiceWorker(registration) {
  const waiting = registration?.waiting;
  if (!waiting || typeof navigator === "undefined" || swReloadPending) return;

  swReloadPending = true;
  navigator.serviceWorker.addEventListener("controllerchange", () => {
    if (typeof window !== "undefined") window.location.reload();
  }, { once: true });
  waiting.postMessage({ type: "SKIP_WAITING" });
}

export function registerAppServiceWorker({ onUpdate, onSuccess } = {}) {
  const diag = getServiceWorkerDiagnostics();
  if (!diag.canRegister) return Promise.resolve(null);
  if (swRegisterPromise) return swRegisterPromise;

  swRegisterPromise = navigator.serviceWorker
    .register(diag.url, { scope: "/" })
    .then((registration) => {
      if (registration.waiting) {
        onUpdate?.(registration);
        activateWaitingServiceWorker(registration);
      }

      registration.addEventListener?.("updatefound", () => {
        const worker = registration.installing;
        if (!worker) return;
        worker.addEventListener("statechange", () => {
          if (worker.state !== "installed") return;
          if (navigator.serviceWorker.controller) {
            onUpdate?.(registration);
            activateWaitingServiceWorker(registration);
            return;
          }
          onSuccess?.(registration);
        });
      });

      return registration;
    })
    .catch((err) => {
      swRegisterPromise = null;
      throw err;
    });

  return swRegisterPromise;
}

export async function unregisterAppServiceWorkers() {
  if (typeof window === "undefined" || !("serviceWorker" in navigator)) return;
  try {
    const registrations = await navigator.serviceWorker.getRegistrations();
    await Promise.all(registrations.map((registration) => registration.unregister()));
  } catch {
    // ignore
  }
}

export async function getReadyServiceWorkerRegistration() {
  if (!canUseServiceWorker()) return null;
  try {
    await registerAppServiceWorker();
    return await navigator.serviceWorker.ready;
  } catch {
    return null;
  }
}
